/**
 * The video standard, enforced.
 *
 * Every video on the site is rendered by engine-video.liquid and driven from
 * here. Nothing in a section decides how a video behaves, because a rule kept
 * in a section is a rule that holds until somebody adds another section.
 *
 * What this file guarantees:
 *
 *   Reduced motion wins outright. If the visitor has asked for less movement
 *   no source is ever attached, so nothing is fetched and nothing moves. The
 *   still and a play control is what they get. It is checked live, so turning
 *   the preference on mid-session stops the videos.
 *
 *   No autoplay on a phone. Below the breakpoint every mode becomes tap, for
 *   the data as much as the motion.
 *
 *   Nothing is fetched before it is wanted. The sources sit in a JSON tag, not
 *   in <source>, so a grid of twenty cards fetches twenty posters and no
 *   video. Hover mode attaches on the first pointer; auto mode when the
 *   element is actually in view.
 *
 *   One playing video per viewport. When several are in view the topmost
 *   plays and the others wait - the lower one waits, not the one that happened
 *   to load first.
 *
 *   The poster is never removed, only covered. If a video fails to load, the
 *   still is still there.
 */
(function () {
  'use strict';

  var MOBILE = window.matchMedia('(max-width: 749px)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
  var registry = [];
  var io = null;

  function sourcesFor(el) {
    var tag = el.querySelector('[data-eo-video-sources]');
    if (!tag) return [];
    try {
      var list = JSON.parse(tag.textContent || '[]');
      return (list || []).filter(function (s) { return s && s.url; });
    } catch (e) {
      return [];
    }
  }

  /* Attach once. Until this runs the browser has fetched nothing. */
  function attach(el) {
    if (el.dataset.attached === 'true') return true;
    var video = el.querySelector('.eo-video__media');
    if (!video) return false;
    var list = sourcesFor(el);
    if (!list.length) return false;

    // mp4 first: it is the one every browser can play, and the browser stops
    // at the first source it accepts
    list.sort(function (a, b) {
      var am = (a.format || '') === 'mp4' ? 0 : 1;
      var bm = (b.format || '') === 'mp4' ? 0 : 1;
      return am - bm;
    });
    list.forEach(function (s) {
      var node = document.createElement('source');
      node.src = s.url;
      if (s.mime_type) node.type = s.mime_type;
      video.appendChild(node);
    });
    el.dataset.attached = 'true';
    video.load();
    return true;
  }

  function show(el) {
    el.classList.add('is-playing');
  }

  function hide(el) {
    el.classList.remove('is-playing');
  }

  function play(el) {
    if (REDUCED.matches) return;
    if (!attach(el)) return;
    var video = el.querySelector('.eo-video__media');
    if (!video) return;
    var p = video.play();
    if (p && p.catch) {
      // a refused play is not an error worth shouting about: the poster is
      // already correct, so the page simply stays still
      p.then(function () { show(el); }).catch(function () { hide(el); });
    } else {
      show(el);
    }
  }

  function stop(el) {
    var video = el.querySelector('.eo-video__media');
    if (video && !video.paused) video.pause();
    hide(el);
  }

  /* Only one autoplaying element at a time, and it is the highest on screen. */
  function arbitrate() {
    if (REDUCED.matches || MOBILE.matches) {
      registry.forEach(function (r) { stop(r.el); });
      return;
    }
    var candidates = registry.filter(function (r) {
      return r.mode === 'auto' && r.visible;
    });
    candidates.sort(function (a, b) {
      return a.el.getBoundingClientRect().top - b.el.getBoundingClientRect().top;
    });
    var winner = candidates[0];
    registry.forEach(function (r) {
      if (r.mode !== 'auto') return;
      if (r === winner) play(r.el);
      else stop(r.el);
    });
  }

  function observe(r) {
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var rec = registry.filter(function (x) { return x.el === entry.target; })[0];
          if (rec) rec.visible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
        });
        arbitrate();
      }, { threshold: [0, 0.5, 1] });
    }
    io.observe(r.el);
  }

  function wire(el) {
    if (el.dataset.wired === 'true') return;
    el.dataset.wired = 'true';
    var mode = el.getAttribute('data-mode') || 'auto';
    var button = el.querySelector('[data-eo-video-play]');
    var video = el.querySelector('.eo-video__media');
    var rec = { el: el, mode: mode, visible: false };
    registry.push(rec);

    /* A phone, or a visitor who asked for less movement, gets the control. */
    function refresh() {
      var tapOnly = MOBILE.matches || REDUCED.matches || mode === 'tap';
      rec.mode = tapOnly ? 'tap' : mode;
      if (button) button.hidden = !tapOnly;
      if (tapOnly) stop(el);
      arbitrate();
    }

    if (button) {
      button.addEventListener('click', function () {
        if (!attach(el)) return;
        if (video.paused) {
          video.play();
          show(el);
          button.hidden = true;
        }
      });
    }

    if (mode === 'hover') {
      // nothing is fetched until a pointer arrives, which is the whole point
      el.addEventListener('mouseenter', function () {
        if (MOBILE.matches || REDUCED.matches) return;
        play(el);
      });
      el.addEventListener('mouseleave', function () {
        stop(el);
        if (video) video.currentTime = 0;
      });
    } else {
      observe(rec);
    }

    [MOBILE, REDUCED].forEach(function (mq) {
      if (mq.addEventListener) mq.addEventListener('change', refresh);
      else if (mq.addListener) mq.addListener(refresh);
    });
    refresh();
  }

  /*
    playsinline on every video, wherever it came from.

    Shopify's media_tag has no playsinline parameter, so the product hero -
    which the platform renders, not us - would go fullscreen on iOS the moment
    it played. On a product page that means the buy button disappearing behind
    a video the customer only wanted to glance at.

    Done here rather than asked for in a comment, because the next person to
    add a video through a Shopify filter will not read the comment.
  */
  function enforceInline(root) {
    (root || document).querySelectorAll('video').forEach(function (v) {
      if (!v.hasAttribute('playsinline')) v.setAttribute('playsinline', '');
      if (!v.hasAttribute('webkit-playsinline')) v.setAttribute('webkit-playsinline', '');
    });
  }

  function scan(root) {
    enforceInline(root);
    (root || document).querySelectorAll('[data-eo-video]').forEach(wire);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); });
  } else {
    scan();
  }

  /*
    Anything that arrives after load gets wired too.

    Section reloads in the theme editor are the obvious case, but the one that
    bites on a live site is the collection grid: filtering and pagination
    replace the cards, and a replaced card whose video nobody wired is a still
    that never plays on hover. A MutationObserver catches both without every
    caller having to remember to tell us.
  */
  var pending = false;
  var mo = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      if (records[i].addedNodes && records[i].addedNodes.length) {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          scan();
        });
        return;
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    registry = registry.filter(function (r) { return !e.target.contains(r.el); });
  });

  window.addEventListener('scroll', function () {
    if (window.__eoVideoTick) return;
    window.__eoVideoTick = true;
    window.requestAnimationFrame(function () {
      window.__eoVideoTick = false;
      arbitrate();
    });
  }, { passive: true });
})();
